import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Calendar, Heart } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const HowItWorks = () => {
  const [heroRef, heroVisible] = useScrollAnimation(0.1);
  const [step1Ref, step1Visible] = useScrollAnimation(0.2);
  const [step2Ref, step2Visible] = useScrollAnimation(0.2);
  const [step3Ref, step3Visible] = useScrollAnimation(0.2);
  const [ctaRef, ctaVisible] = useScrollAnimation(0.2);

  return (
    <div className="min-h-screen bg-cream page-transition">
      {/* Hero Section */}
      <section 
        ref={heroRef}
        className="relative h-96 flex items-center justify-center text-white overflow-hidden"
      >
        <div 
          className="absolute inset-0 hero-bg-zoom"
          style={{
            backgroundImage: "url('https://customer-assets.emergentagent.com/job_client-web-setup/artifacts/tqhnalfr_IMG_0101.jpeg')",
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="absolute inset-0 image-overlay-luxury"></div>
        <div className={`relative z-10 text-center ${heroVisible ? 'animate-fade-in-up' : 'opacity-0'}`} data-testid="how-it-works-hero">
          <CheckCircle className="w-16 h-16 mx-auto mb-4 text-gold" />
          <h1 className="text-4xl md:text-6xl font-bold text-shadow-luxury" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>How It Works</h1>
          <div className="w-24 h-1 bg-gold mx-auto mt-4"></div>
          <p className="text-2xl mt-6 italic">Your Journey to a Perfect Sunday</p>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-20 px-4 bg-cream-dark">
        <div className="max-w-5xl mx-auto">
          {/* Step 1 */}
          <div ref={step1Ref} className={`mb-20 ${step1Visible ? 'animate-fade-in-up' : 'opacity-0'}`}>
            <div className="flex flex-col md:flex-row items-start gap-8">
              <div className="flex-shrink-0">
                <div className="w-20 h-20 bg-gold rounded-full flex items-center justify-center text-white text-3xl font-bold" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>
                  1
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-4xl font-bold mb-4 text-brown" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>1. Gather Your Family or Friends</h3>
                <p className="text-xl text-gray-600 leading-relaxed">
                  Our doors are open for everyone who loves exquisite food and good company. Bring your people, and we'll treat them like ours.
                </p>
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div ref={step2Ref} className={`mb-20 ${step2Visible ? 'animate-fade-in-up' : 'opacity-0'}`}>
            <div className="flex flex-col md:flex-row items-start gap-8">
              <div className="flex-shrink-0">
                <div className="w-20 h-20 bg-gold rounded-full flex items-center justify-center text-white text-3xl font-bold" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>
                  2
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-4xl font-bold mb-4 text-brown" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>2. Arrive & Indulge</h3>
                <p className="text-xl text-gray-600 leading-relaxed mb-6">
                  Step into a warm, welcoming atmosphere where everything is already set for you. From the moment you walk in, you're treated like family.
                </p>
                <p className="text-xl text-gray-600 leading-relaxed">
                  Explore our elegant buffet filled with authentic Afro Caribbean dishes — from jerk chicken and oxtail to rice & peas, plantain, and so much more. All cooked fresh, with love.
                </p>
              </div>
            </div>
          </div>

          {/* Step 3 */}
          <div ref={step3Ref} className={`${step3Visible ? 'animate-fade-in-up' : 'opacity-0'}`}>
            <div className="flex flex-col md:flex-row items-start gap-8">
              <div className="flex-shrink-0">
                <div className="w-20 h-20 bg-gold rounded-full flex items-center justify-center text-white text-3xl font-bold" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>
                  3
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-4xl font-bold mb-4 text-brown" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>3. Relax & Connect</h3>
                <p className="text-xl text-gray-600 leading-relaxed mb-6">
                  Sit back, enjoy live music, share stories, laugh, and savour every moment. This is your time to be present, to connect, and to feel at home.
                </p>
                <p className="text-xl text-gray-600 leading-relaxed">
                  Whether it's catching up with old friends or making new memories with your family, Mama-Mam's is your place to gather, celebrate, and belong.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-1 bg-gold mx-auto mb-8"></div>
          <h2 className="text-5xl font-bold text-brown mb-12" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>Why Choose Mama-Mam's Kitchen?</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-8 card-hover bg-cream-dark rounded-lg">
              <Heart className="w-12 h-12 text-gold mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-brown mb-4" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>Made with Love</h3>
              <p className="text-gray-600 leading-relaxed">
                Every dish is prepared with care, honouring the legacy of Mama-Mam's home cooking.
              </p>
            </div>

            <div className="p-8 card-hover bg-cream-dark rounded-lg">
              <CheckCircle className="w-12 h-12 text-gold mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-brown mb-4" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>Authentic Flavours</h3>
              <p className="text-gray-600 leading-relaxed">
                Experience genuine Afro Caribbean cuisine that takes you back to family Sunday dinners.
              </p>
            </div>

            <div className="p-8 card-hover bg-cream-dark rounded-lg">
              <Calendar className="w-12 h-12 text-gold mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-brown mb-4" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>Sunday Tradition</h3>
              <p className="text-gray-600 leading-relaxed">
                An elegant, welcoming space where everyone is treated like family. Just like <strong>Mama-Mam</strong> taught us.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section ref={ctaRef} className="py-20 px-4 bg-gold text-center">
        <div className={`max-w-4xl mx-auto ${ctaVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
          <h2 className="text-5xl font-bold text-white mb-8" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>Ready for Your Sunday Experience?</h2>
          <p className="text-2xl mb-10 text-white opacity-90">
            Join us this Sunday and rediscover the joy of gathering around good food and great company.
          </p>
          <Link 
            to="/reserve" 
            className="inline-block bg-white text-brown hover:bg-cream font-semibold px-10 py-4 rounded-full text-xl shadow-lg btn-luxury"
          >
            Book Your Table →
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HowItWorks;
